import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  ListSubheader,
} from '@mui/material'
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Description as InspectionIcon,
  ShowChart as DataIcon,
  Notifications as AlertIcon,
  DeviceHub as DevicesIcon,
  Checklist as ChecklistIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Science as ResearchIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material'

interface NavDrawerProps {
  open: boolean
  onClose: () => void
  variant?: 'permanent' | 'persistent' | 'temporary'
  width?: number
}

const NavDrawer: React.FC<NavDrawerProps> = ({
  open,
  onClose,
  variant = 'temporary',
  width = 280
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleNavigation = (path: string) => {
    navigate(path)
    if (isMobile) {
      onClose()
    }
  }

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true
    if (path !== '/' && location.pathname.startsWith(path)) return true
    return false
  }

  const menuGroups = [
    {
      subheader: 'Overview',
      items: [
        { title: 'Hives', icon: <HomeIcon />, path: '/' },
        { title: 'Dashboards', icon: <DashboardIcon />, path: '/dashboards' },
        { title: 'Alerts', icon: <AlertIcon />, path: '/alerts' },
      ]
    },
    {
      subheader: 'Monitoring',
      items: [
        { title: 'Trends', icon: <DataIcon />, path: '/measurements' },
        { title: 'Devices', icon: <DevicesIcon />, path: '/devices' },
      ]
    },
    {
      subheader: 'Records',
      items: [
        { title: 'Inspections', icon: <InspectionIcon />, path: '/inspections' },
        { title: 'Checklists', icon: <ChecklistIcon />, path: '/checklists' },
      ]
    },
    {
      subheader: 'Collaborate',
      items: [
        { title: 'Groups', icon: <GroupIcon />, path: '/groups' },
        { title: 'Research', icon: <ResearchIcon />, path: '/research' },
      ]
    },
    {
      subheader: 'Data',
      items: [
        { title: 'Export', icon: <ExportIcon />, path: '/export' },
        { title: 'Import', icon: <ImportIcon />, path: '/import' },
      ]
    },
    {
      subheader: 'Configuration',
      items: [
        { title: 'Alert Rules', icon: <SettingsIcon />, path: '/alertrules' },
      ]
    }
  ]

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header - Logo */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 56,
      }}>
        <Box
          component="img"
          src="/img/logo.png"
          alt="Smart Nyuki Logo"
          sx={{
            height: 40,
            width: 'auto',
          }}
        />
        {isMobile && (
          <IconButton onClick={onClose} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {menuGroups.map((group, index) => (
          <List
            key={group.subheader}
            subheader={
              <ListSubheader component="div" sx={{
                lineHeight: '2rem',
                backgroundColor: '#fafafa',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mt: index === 0 ? 0 : 1,
                position: 'relative', // Changed from sticky
                top: 'auto',
                zIndex: 1,
              }}>
                {group.subheader}
              </ListSubheader>
            }
          >
            {group.items.map((item) => {
              const active = isActive(item.path)
              return (
                <ListItem key={item.title} disablePadding sx={{ mb: 0.5, px: 2 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={active}
                    sx={{
                      borderRadius: 1.5,
                      py: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&.Mui-selected': {
                        backgroundColor: '#FFF8E1', // Very light yellow
                        color: '#F8B133', // Primary yellow
                        '& .MuiListItemIcon-root': {
                          color: '#F8B133',
                        },
                        '&:hover': {
                          backgroundColor: '#FFF3CD',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 36,
                      color: active ? '#F8B133' : 'text.secondary',
                      transition: 'color 0.2s',
                      '& svg': { fontSize: '1.25rem' }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontWeight: active ? 600 : 500,
                        fontSize: '0.85rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
            {index < menuGroups.length - 1 && <Divider sx={{ my: 1, mx: 2 }} variant="middle" />}
          </List>
        ))}
      </Box>

      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          v1.0.0
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant={variant}
      sx={{
        width: width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#fafafa',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default NavDrawer
